import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import DialogContentText from '@mui/material/DialogContentText'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputBase from '@mui/material/InputBase'
import InputLabel from '@mui/material/InputLabel'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Snackbar from '@mui/material/Snackbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import makeStyles from '@mui/styles/makeStyles'
import { useTheme } from '@mui/styles'
import moment from 'moment-timezone'

import { useHistory, useParams } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'

import { FirebaseContext } from '../../utils/firebase'
import 'firebase/auth'
import { useEnhancerPlatforms } from '../../contexts/EnhancerContext'

import { API_ROOT_URL } from '../../constants'
import * as ROUTES from '../../constants/routes'

import hyrosLogoImg from '../../media/hyros_logo.png'

const TIMEZONE = moment.tz.guess() // This should be set by user configuration

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    left: 66,
    right: 0,
    width: 'calc(100% - 66px)',
    height: '100vh',
    overflowY: 'auto',
    backgroundColor: theme.palette.grey[50],
  },
  logoImg: {
    width: 100,
  },
  syncButton: {
    width: 110,
  },
  redButton: {
    color: theme.palette.error.main,
    '&:hover': {
      background: 'rgba(255,23,68,0.04)',
    },
  },
}))

const REVOKE_SUCCESS_MESSAGE = 'Account removed. Data will no longer be enhanced from this account. Redirecting back to settings...'
const SYNC_SUCCESS_MESSAGE = 'Sync initialized. Processing may take a while, and you can safely leave this page.'
const SYNC_ERROR_MESSAGE = 'Sync failed to initialize. Try again and contact support if error persists.'

const dataTypeMenuItems = [
  { value: 'all', display: 'Payments and Contacts'},
  { value: 'payment', display: 'Payments'},
  { value: 'contact', display: 'Contacts'},
]

const dateRangeMenuItems = [
  { value: 'last_7_days', display: 'Last 7 days'},
  { value: 'last_30_days', display: 'Last 30 days'},
  { value: 'last_60_days', display: 'Last 60 days'},
  { value: 'last_3_months', display: 'Last 3 months'},
  { value: 'last_6_months', display: 'Last 6 months'},
  { value: 'last_9_months', display: 'Last 9 months'},
  { value: 'last_12_months', display: 'Last 12 months'},
  { value: 'last_2_years', display: 'Last 2 years'},
  { value: 'last_3_years', display: 'Last 3 years'},
  { value: 'ytd', display: 'Year to date'},
  { value: 'last_and_this_year', display: 'Last year and this year'},
  { value: 'all_time', display: 'All time'},
]

const HyrosEnhancer = () => {
  const classes = useStyles()
  const theme = useTheme()
  const { id } = useParams()
  const history = useHistory()
  const firebase = React.useContext(FirebaseContext)
  const { fetchEnhancerPlatforms } = useEnhancerPlatforms()

  const [showCopyTooltip, setShowCopyTooltip] = React.useState(false)

  const [message, setMessage] = React.useState('')
  const [showMessage, setShowMessage] = React.useState(false)
  const [messageSeverity, setMessageSeverity] = React.useState('success')
  const [showRevokeConfirmation, setShowRevokeConfirmation] = React.useState(false)
  const [initialIntegrationIds, setInitialIntegrationIds] = React.useState([])
  const [selectedIntegrationIds, setSelectedIntegrationIds] = React.useState([])

  const [dataType, setDataType] = React.useState('all')
  const [dateRange, setDateRange] = React.useState('last_30_days')
  const [startDate, setStartDate] = React.useState('')
  const [endDate, setEndDate] = React.useState('')

  React.useEffect(() => {
    setDatesFromRange(dateRange)
  }, [dateRange])

  const {
    isLoading: isLoadingEnhancer,
    data: enhancer = {}
  } = useQuery(['enhancer-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 15 * 60 * 1000,  // 15 minutes
      staleTime: 15 * 60 * 1000,  // 15 minutes
      refetchOnWindowFocus: false,
    }
  )

  const {
    isLoading: isLoadingEnhancerIntegrationIds,
  } = useQuery(['enhancer-integrations-get', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/${id}/integrations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          setInitialIntegrationIds(data)
          setSelectedIntegrationIds(data)
        }
      }
    }
  )

  const { isLoading: isLoadingRevoke, refetch: revoke } = useQuery(['revoke-hyros'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/hyros/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enhancer_id: id
        })
      }).then(res => res.json())
    }),
    {
      enabled: false,
      staleTime: Infinity,
      refetchOnReconnect: false,
      onSuccess: () => {
        setShowRevokeConfirmation(false)
        setMessage(REVOKE_SUCCESS_MESSAGE)
        setMessageSeverity('success')
        setShowMessage(true)
        fetchEnhancerPlatforms()
        setTimeout(() => history.push(ROUTES.SETTINGS), 3000)
      },
    }
  )

  const { isLoading: isUpdatingEnhancerIntegrations, mutate: updateEnhancerIntegrations } = useMutation(
    newIntegrationIds => firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/integrations`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enhancer_id: id,
          integration_ids: newIntegrationIds,
        }),
      }).then(res => res.json())
    }),
    {
      onSuccess: () => {
        setInitialIntegrationIds(selectedIntegrationIds)
        setShowMessage(true)
        setMessage('Data Sources successfully updated.')
        setMessageSeverity('success')
      },
      onError: () => {
        setSelectedIntegrationIds(initialIntegrationIds)
        setShowMessage(true)
        setMessage('Error updating Data Sources. Selected sources reverted. Please try again, and if problem persists, contact support.')
        setMessageSeverity('error')
      }
    }
  )

  const {
    isLoading: isLoadingIntegrations,
    data: integrations = []
  } = useQuery(['integrations-list'], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/integrations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      }).then(res => res.json())
    }),
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  const { isFetching: isSyncing, refetch: sync } = useQuery(['enhancer-sync', id], () =>
    firebase.auth().currentUser.getIdToken(false).then(token => {
      return fetch(`${API_ROOT_URL}/api_fs/enhancers/sync`, {
        method: 'POST',
        body: JSON.stringify({
          enhancer_id: id,
          dataType,
          startDate,
          endDate,
        }),
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      }).then(res => res.json())
    }),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      staleTime: 0,
      onSuccess: () => {
        setShowMessage(true)
        setMessage(SYNC_SUCCESS_MESSAGE)
        setMessageSeverity('success')
      },
      onError: () => {
        setShowMessage(true)
        setMessage(SYNC_ERROR_MESSAGE)
        setMessageSeverity('error')
      }
    }
  )

  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false
    const sortedA = [...a].sort()
    const sortedB = [...b].sort()
    return sortedA.every((value, index) => value === sortedB[index])
  }

  const handleCloseMessage = (_, reason) => {
    if (reason === 'clickaway') return
    setShowMessage(false)
  }

  const handleIntegrationChange = (integrationId) => {
    setSelectedIntegrationIds(prevSelected => {
      if (prevSelected.includes(integrationId)) {
        return prevSelected.filter(id => id !== integrationId)
      } else {
        return [...prevSelected, integrationId]
      }
    })
  }

  const handleClear = () => {
    setSelectedIntegrationIds([])
  }

  const handleSelectAll = () => {
    setSelectedIntegrationIds(integrations.map(integration => integration.id))
  }

  const handleUpdateEnhancerIntegrations = () => {
    updateEnhancerIntegrations(selectedIntegrationIds)
  }

  const setDatesFromRange = (dateRange) => {
    let startDate, endDate
    switch (dateRange) {
      case 'last_7_days':
        startDate = moment.tz(TIMEZONE).startOf('day').subtract(7, 'days').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'last_30_days':
        startDate = moment.tz(TIMEZONE).startOf('day').subtract(30, 'days').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'last_60_days':
        startDate = moment.tz(TIMEZONE).startOf('day').subtract(60, 'days').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'last_3_months':
        startDate = moment.tz(TIMEZONE).startOf('day').subtract(3, 'months').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'last_6_months':
        startDate = moment.tz(TIMEZONE).startOf('day').subtract(6, 'months').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'last_9_months':
        startDate = moment.tz(TIMEZONE).startOf('day').subtract(9, 'months').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'last_12_months':
        startDate = moment.tz(TIMEZONE).startOf('day').subtract(12, 'months').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'last_2_years':
        startDate = moment.tz(TIMEZONE).startOf('day').subtract(2, 'years').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'last_3_years':
        startDate = moment.tz(TIMEZONE).startOf('day').subtract(3, 'years').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'ytd':
        startDate = moment.tz(TIMEZONE).startOf('year').startOf('day').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'last_and_this_year':
        startDate = moment.tz(TIMEZONE).subtract(1, 'year').startOf('year').startOf('day').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      case 'all_time':
        startDate = moment.tz('1970', TIMEZONE).startOf('day').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
      default:
        startDate = moment.tz(TIMEZONE).startOf('day').subtract(12, 'months').toISOString()
        endDate = moment.tz(TIMEZONE).endOf('day').toISOString()
        break
    }
    setStartDate(startDate)
    setEndDate(endDate)
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    setShowCopyTooltip(true)
  }

  const maskText = (apiKey) => {
    if (!apiKey) return ''
    const visibleChars = 4
    const maskedSection = apiKey.slice(visibleChars, -visibleChars).replace(/./g, '*')
    return apiKey.slice(0, visibleChars) + maskedSection + apiKey.slice(-visibleChars)
  }

  const dataSourcesChanged = !arraysEqual(initialIntegrationIds, selectedIntegrationIds)

  return (
    <div className={classes.root}>
      <Box padding={2}>
        <Button
          variant='outlined'
          onClick={() => history.push(ROUTES.SETTINGS)}
          size='small'
        >
          Back
        </Button>
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={2}>
                  <img
                    className={classes.logoImg}
                    alt='hyros_logo'
                    src={hyrosLogoImg}
                  />
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='h5' gutterBottom>
                    Hyros Enhancer
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Account ID */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Account ID
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box height='100%' display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      {isLoadingEnhancer ? <Skeleton variant='text' width={100} /> : `${enhancer.platform_id}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Name */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Name
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box height='100%' display='flex' alignItems='center'>
                    <Typography variant='body2'>
                      {isLoadingEnhancer ? <Skeleton variant='text' width={100} /> : `${enhancer.nickname}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* API Key */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    API Key
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box display='flex' alignItems='center'>
                    {isLoadingEnhancer ? <Skeleton variant='text' width={100} /> : (
                      <Box display='flex' flexDirection='row' width='100%'>
                        <InputBase
                          variant='filled'
                          value={maskText(enhancer.api_key)}
                          size='small'
                          fullWidth
                          disabled
                        />
                        <Tooltip
                          open={showCopyTooltip}
                          onClose={() => setShowCopyTooltip(false)}
                          leaveDelay={2000}
                          placement='right'
                          title='Copied'
                        >
                          <Button
                            variant='text'
                            color='primary'
                            onClick={() => copyToClipboard(enhancer.api_key)}
                          >
                            Copy
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Data Sources */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Data Sources
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Box display='block'>
                    <Typography variant='body2' paragraph>
                      Select the Data Sources that you would like to be enhanced by this Hyros Enhancer. By selecting a Data Source below,
                      any contact or payment data that comes from that Data Source will then be enhanced by Hyros to populate additional fields
                      that will be available in LTV Numbers.
                    </Typography>
                    <Typography variant='body2' paragraph>
                      There will be a short delay before enhancing a record to ensure that data is available in Hyros when requested.
                    </Typography>
                  </Box>

                  {/* Integrations List */}
                  <Box
                    marginTop={1}
                    marginBottom={1}
                    border={1}
                    borderColor={theme.palette.divider}
                    borderRadius={1}
                    padding={1}
                    maxHeight={400}
                    overflow='auto'
                  >
                    {isLoadingIntegrations || isLoadingEnhancerIntegrationIds ? (
                      <Skeleton variant='text' width='100%' />
                    ) : (
                      <List
                        dense
                      >
                        {integrations
                          .sort((a, b) => a.nickname.localeCompare(b.nickname))
                          .map(integration => {
                            const labelId = `checkbox-list-label-${integration.id}`
                            return (
                              <ListItem
                                key={integration.id}
                                dense
                                disablePadding
                                sx={{ bgcolor: selectedIntegrationIds.includes(integration.id) ? theme.palette.action.selected : 'inherit' }}
                              >
                                <ListItemButton
                                  onClick={() => handleIntegrationChange(integration.id)}
                                  dense
                                  sx={{ height: 40 }}
                                >
                                  <ListItemIcon>
                                    <Checkbox
                                      edge='start'
                                      checked={selectedIntegrationIds.includes(integration.id)}
                                      tabIndex={-1}
                                      disableRipple
                                      inputProps={{ 'aria-labelledby': labelId }}
                                    />
                                  </ListItemIcon>
                                  <ListItemText id={labelId} primary={`${integration.nickname} (${integration.platform})`} />
                                </ListItemButton>
                              </ListItem>
                            )
                          })}
                      </List>
                    )}
                  </Box>
                  <Box>
                    <Typography variant='body2'>
                      {selectedIntegrationIds.length} Data Sources selected for enhancement
                      {dataSourcesChanged && (<span>&nbsp;<b>(unsaved changes)</b></span>)}
                    </Typography>
                  </Box>
                  <Box marginTop={2} display='flex' justifyContent='flex-start' columnGap={1}>
                    <Button variant='outlined' onClick={handleClear}>Clear</Button>
                    <Button variant='outlined' onClick={handleSelectAll}>Select All</Button>
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={handleUpdateEnhancerIntegrations}
                      disabled={!dataSourcesChanged}
                    >
                      {isUpdatingEnhancerIntegrations ? <CircularProgress color='inherit' size={24} /> : 'Save'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Enhancer sync for selected integrations */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Sync
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' paragraph>
                    If you want to enhance existing historical data in your LTV Numbers account for your selected integrations, you can do so by selecting the data type and date range below.
                    This will start a process of enhancing existing data inside LTV Numbers associated with each selected integration over the date range selected.
                    Note that the date range is based on when sales occurred or when contacts were created, not when the data was sent to LTV Numbers.
                  </Typography>
                  <Typography variant='body2' paragraph>
                    Due to rate limitations, this process will take anywhere from about 15 minutes to several hours or more, depending on the amount of data to be enhanced.
                  </Typography>

                  <Typography variant='h6' gutterBottom>
                    Data Type
                  </Typography>
                  <Typography variant='body2' paragraph>
                    There are two types of data that can be enhanced: Payments and Contacts. Select the type of data you want to enhance.
                  </Typography>
                  <Typography variant='body2' paragraph>
                    To enhance your sales data, choose <b>Payments</b>. To enhance your contact origin data, choose <b>Contacts</b>. Or, choose <b>Payments and Contacts</b> to enhance both.
                  </Typography>
                  <Typography variant='body2' paragraph>
                    In most cases, you will want to select <b>Payments and Contacts</b> for completeness and clarity on the resulting enhanced data. If you are unsure what to choose, reach out to your LTV Numbers
                    account manager for help.
                  </Typography>

                  <Box marginTop={2} display='flex' alignItems='center'>
                    <FormControl
                      variant='outlined'
                      margin='dense'
                      sx={{ margin: 1, minWidth: 150 }}
                      required
                     >
                      <InputLabel id='data-type-label'>Data Type</InputLabel>
                      <Select
                        labelId='data-type-label'
                        id='data-type-select'
                        value={dataType}
                        onChange={event => setDataType(event.target.value)}
                        label='Data Type'
                      >
                        {dataTypeMenuItems.map(item =>
                          <MenuItem key={item.value} value={item.value}>{item.display}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <FormControl
                      variant='outlined'
                      margin='dense'
                      sx={{ margin: 1, minWidth: 150 }}
                      required
                     >
                      <InputLabel id='data-type-label'>Date Range</InputLabel>
                      <Select
                        labelId='date-range-label'
                        id='date-range-select'
                        value={dateRange}
                        onChange={event => setDateRange(event.target.value)}
                        label='Date Range'
                      >
                        {dateRangeMenuItems.map(item =>
                          <MenuItem key={item.value} value={item.value}>{item.display}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box>
                    <Button
                      variant='contained'
                      color='secondary'
                      onClick={() => sync()}
                      sx={{ width: 110 }}
                      disabled={!dataType || !dateRange }
                    >
                      {isSyncing ?
                        <CircularProgress color='inherit' size={24} />
                        : 'Sync Data'
                      }
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>

        {/* Delete Enhancer */}
        <Box marginTop={2}>
          <Paper>
            <Box padding={2}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant='h6'>
                    Delete
                  </Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant='body2' gutterBottom>
                    If you want to remove this data enhancer so transacations can no longer be saved, click the button below.
                  </Typography>
                  <Button
                    className={classes.redButton}
                    variant='text'
                    onClick={() => setShowRevokeConfirmation(true)}
                  >
                    Delete Enhancer
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Dialog
        open={showRevokeConfirmation}
        onClose={() => setShowRevokeConfirmation(false)}
      >
        <DialogTitle>
          Remove access to <b>{enhancer.nickname}</b>?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will prevent data from being enhanced.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.redButton}
            variant='text'
            onClick={() => setShowRevokeConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            onClick={() => revoke()}
          >
            {isLoadingRevoke ? <CircularProgress color='inherit' size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        autoHideDuration={3000}
        onClose={handleCloseMessage}
      >
        <Alert
          severity={messageSeverity}
          variant='filled'
          onClose={handleCloseMessage}
        >
          {message}
        </Alert>
      </Snackbar>
    </div>
  )
}

HyrosEnhancer.propTypes = {}

export default HyrosEnhancer